import { Box, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { USER_BADGE_COLORS } from '../userBadgeColors';
import {
  terrainCoverageCount,
  fromPayload,
  unresolvedNodes,
  type PerimeterNode,
  type PerimeterTree,
} from './perimeterTree.logic';

export interface PerimeterChipsProps {
  nodes: PerimeterNode[];
  tree: PerimeterTree;
}

/**
 * « Périmètre actif » column of the users list (HOMCO-1249). « Tous les pays » (no
 * counter) when the root is retained; otherwise up to two chips, then « +N nœuds »
 * for the remaining retained nodes, « +N hérités » for the inherited ones, and raw
 * chips for nodes the tree can't resolve (so a stored perimeter is never shown empty).
 */
export function PerimeterChips({ nodes, tree }: PerimeterChipsProps) {
  const { t } = useTranslation('users');
  const roots = fromPayload(nodes, tree);
  const unknown = unresolvedNodes(nodes, tree);

  // Only a truly empty perimeter shows the empty state (not a not-yet-loaded tree).
  const allColors = USER_BADGE_COLORS.allCountries;
  const defColors = USER_BADGE_COLORS.default;

  if (nodes.length === 0) {
    return (
      <Chip
        size="small"
        variant="outlined"
        label={t('perimeterChips.empty')}
        sx={{ color: defColors.color }}
      />
    );
  }

  if (roots.includes('all')) {
    return (
      <Chip
        size="small"
        label={t('perimeter.all')}
        sx={{ color: allColors.color, bgcolor: allColors.bgcolor }}
      />
    );
  }

  const shown = roots.slice(0, 2);
  const extra = roots.length - shown.length;
  const inherited = terrainCoverageCount(roots, tree) - roots.filter(id => tree.byId[id]?.type === 'terrain').length;

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
      {shown.map(id => (
        <Chip
          key={id}
          size="small"
          label={tree.byId[id].label}
          sx={{ color: defColors.color, bgcolor: defColors.bgcolor }}
        />
      ))}
      {extra > 0 && (
        <Chip
          size="small"
          label={t('perimeterChips.moreNodes', { count: extra })}
          sx={{ color: defColors.color, bgcolor: defColors.bgcolor }}
        />
      )}
      {inherited > 0 && (
        <Chip
          size="small"
          variant="outlined"
          label={t('perimeterChips.moreInherited', { count: inherited })}
          sx={{ color: defColors.color }}
        />
      )}
      {unknown.map((node, i) => (
        <Chip
          key={`raw-${node.type}-${node.code ?? i}`}
          size="small"
          variant="outlined"
          label={node.type === 'all' ? t('perimeter.all') : (node.code ?? '—')}
          sx={{ color: defColors.color }}
        />
      ))}
    </Box>
  );
}
